import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  browserSupportsWebAuthn,
  startRegistration,
} from '@simplewebauthn/browser';
import { auth, type PasskeyListItem } from '../lib/api.js';
import { PageHeader, SectionLabel, Card, SampleBanner, StatusPill, Row } from '../components/ui.js';

interface Integration {
  name: string;
  status: 'connected' | 'pending' | 'disconnected';
  detail: string;
}
interface TeamMember {
  name: string;
  email: string;
  role: 'owner' | 'manager' | 'viewer';
  last_login: string;
}

const INTEGRATIONS: Integration[] = [
  { name: 'WordPress (REST API)', status: 'connected', detail: 'glistendentalstudio.com · Application Password set' },
  { name: 'Google Business Profile', status: 'connected', detail: '3 locations claimed + verified' },
  { name: 'Google Search Console', status: 'connected', detail: 'Domain property authorized' },
  { name: 'DataForSEO (rank tracking)', status: 'connected', detail: 'Active · last sweep 2:14 AM today' },
  { name: 'Stripe (billing)', status: 'connected', detail: 'Pro tier · card ending 4242' },
  { name: 'GA4 (conversion)', status: 'pending', detail: 'Awaiting service account permission' },
  { name: 'BrightLocal (citations)', status: 'pending', detail: 'API key requested · ETA 1-2 days' },
];

const TEAM: TeamMember[] = [
  { name: 'Dr. Revan Dawood', email: 'revan@glistendental.com', role: 'owner', last_login: '2026-05-04T18:42:00Z' },
  { name: 'Filza Tirmizi', email: 'filza@glistendental.com', role: 'manager', last_login: '2026-05-04T15:11:00Z' },
  { name: 'Aubrey Hanks', email: 'aubrey@glistendental.com', role: 'viewer', last_login: '2026-05-03T22:08:00Z' },
];

const KEYWORDS_PER_LOCATION = {
  'Glisten Gilbert': ['dentist gilbert az', 'emergency dentist gilbert', 'family dentist gilbert az', 'dental implants gilbert', 'composite fillings gilbert az', 'teeth whitening gilbert', 'wisdom tooth removal gilbert', 'gentle dentist for kids gilbert'],
  'Glisten Mesa': ['dentist mesa az', 'pediatric dentist mesa', 'invisalign mesa', 'ahcccs dentist mesa', 'root canal mesa', 'dental cleaning mesa', 'dentist that takes delta dental mesa', 'dentist for nervous patients mesa'],
  'Glisten Glendale': ['dentist glendale az', 'same day crowns glendale', 'sedation dentistry glendale', 'cosmetic dentist glendale az', 'crowns and bridges glendale', 'tooth extraction near me'],
};

const BANNED_PHRASES = ['dive deep', 'in today\'s digital landscape', 'unlock', 'leverage', 'navigate the complexities', 'cutting-edge', 'world-class', 'revolutionary', 'game-changer', 'best-in-class'];

const SERVICE_AREAS = ['Gilbert AZ', 'Mesa AZ', 'Glendale AZ', 'Chandler AZ', 'Tempe AZ', 'Phoenix AZ', 'Peoria AZ', 'Scottsdale AZ'];

export function SettingsPage() {
  return (
    <div className="space-y-12">
      <PageHeader
        eyebrow="Settings"
        title={<>Voice, keywords, integrations, <em className="text-cream/80">team.</em></>}
        subtitle="Everything Bloom uses to write in your voice and target the queries that bring you patients."
      />
      <SampleBanner note="Sample configuration shown for reference. Real settings flow lives in the customer onboarding wizard (Phase E). Security section below is live." />

      <SecuritySection />

      <section>
        <SectionLabel>Brand voice</SectionLabel>
        <Card>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
            <Row label="Voice preset">Trusted family practice — warm, plainspoken, evidence-led</Row>
            <Row label="First-person pronoun">we (the team)</Row>
            <Row label="Reading level target">8th grade</Row>
            <Row label="Avg post length">1,200-1,500 words</Row>
          </dl>
          <div className="mt-6">
            <div className="text-[10px] uppercase tracking-[0.22em] text-cream/40 mb-2">Banned phrases ({BANNED_PHRASES.length})</div>
            <div className="flex flex-wrap gap-2">
              {BANNED_PHRASES.map((p) => (
                <span key={p} className="text-[11px] px-2.5 py-1 rounded-full bg-rose-500/[0.06] text-rose-200/70 border border-rose-300/[0.14]">
                  {p}
                </span>
              ))}
            </div>
          </div>
        </Card>
      </section>

      <section>
        <SectionLabel>Tracked keywords ({Object.values(KEYWORDS_PER_LOCATION).flat().length})</SectionLabel>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Object.entries(KEYWORDS_PER_LOCATION).map(([account, kws]) => (
            <Card key={account}>
              <div className="text-[10px] uppercase tracking-[0.22em] text-cream/40 mb-3">{account}</div>
              <div className="space-y-1.5">
                {kws.map((k) => (
                  <div key={k} className="text-sm text-cream/75">{k}</div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      </section>

      <section>
        <SectionLabel>Service areas</SectionLabel>
        <Card>
          <div className="flex flex-wrap gap-2">
            {SERVICE_AREAS.map((a) => (
              <span key={a} className="text-[11px] px-3 py-1 rounded-full bg-cream/[0.06] text-cream/70 border border-cream/[0.12]">
                {a}
              </span>
            ))}
          </div>
        </Card>
      </section>

      <section>
        <SectionLabel>Integrations</SectionLabel>
        <div className="rounded-2xl border border-cream/10 bg-cream/[0.02] overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-cream/40 border-b border-cream/[0.06]">
                <th className="px-5 py-3 font-medium">Service</th>
                <th className="px-3 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium">Detail</th>
              </tr>
            </thead>
            <tbody>
              {INTEGRATIONS.map((i) => (
                <tr key={i.name} className="border-b border-cream/[0.04] last:border-0 hover:bg-cream/[0.02] transition-colors">
                  <td className="px-5 py-3 text-cream/85">{i.name}</td>
                  <td className="px-3 py-3"><StatusPill tone={i.status === 'connected' ? 'ok' : i.status === 'pending' ? 'warn' : 'bad'}>{i.status}</StatusPill></td>
                  <td className="px-5 py-3 text-cream/65 text-[12px]">{i.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <SectionLabel>Team</SectionLabel>
        <div className="rounded-2xl border border-cream/10 bg-cream/[0.02] overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-cream/40 border-b border-cream/[0.06]">
                <th className="px-5 py-3 font-medium">Name</th>
                <th className="px-3 py-3 font-medium">Email</th>
                <th className="px-3 py-3 font-medium">Role</th>
                <th className="px-5 py-3 font-medium">Last login</th>
              </tr>
            </thead>
            <tbody>
              {TEAM.map((m) => (
                <tr key={m.email} className="border-b border-cream/[0.04] last:border-0 hover:bg-cream/[0.02] transition-colors">
                  <td className="px-5 py-3 text-cream/85">{m.name}</td>
                  <td className="px-3 py-3 text-cream/65 text-[12px]">{m.email}</td>
                  <td className="px-3 py-3"><StatusPill tone={m.role === 'owner' ? 'info' : 'neutral'}>{m.role}</StatusPill></td>
                  <td className="px-5 py-3 text-cream/65 text-[12px]">{new Date(m.last_login).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function SecuritySection() {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const passkeys = useQuery<{ credentials: PasskeyListItem[] }>({
    queryKey: ['auth', 'passkeys'],
    queryFn: auth.listPasskeys,
    staleTime: 30_000,
  });

  const supportsPasskey = browserSupportsWebAuthn();

  const onAddPasskey = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const begin = await auth.passkeyRegisterBegin();
      // @ts-expect-error — server returns full WebAuthn options shape; types differ slightly across @simplewebauthn versions
      const credResp = await startRegistration({ optionsJSON: begin.options });
      const result = await auth.passkeyRegisterFinish(begin.challenge_id, credResp);
      setSuccess(`Passkey added (registered ${new Date(result.registered_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric' })}).`);
      await qc.invalidateQueries({ queryKey: ['auth', 'passkeys'] });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Passkey enrollment failed.';
      if (msg.includes('NotAllowed') || msg.toLowerCase().includes('cancel')) {
        setError(null);
      } else {
        setError(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  const creds = passkeys.data?.credentials ?? [];
  const hasNone = !passkeys.isLoading && creds.length === 0;

  return (
    <section>
      <SectionLabel>Security</SectionLabel>
      <Card>
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="text-cream/90 font-medium mb-1">Passkeys</div>
            <p className="text-sm text-cream/55 max-w-md">
              Use Touch ID, Face ID, Windows Hello, or a security key to sign in without typing an email.
              Passkeys are phishing-resistant and never leave your device.
            </p>
          </div>
          <div className="flex items-center gap-3">
            {supportsPasskey ? (
              <button
                type="button"
                onClick={() => { void onAddPasskey(); }}
                disabled={busy}
                className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] px-4 py-2 rounded-full bg-cream/[0.08] text-cream hover:bg-cream/[0.14] border border-cream/[0.16] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                {busy ? 'Waiting…' : 'Add passkey'}
              </button>
            ) : (
              <StatusPill tone="warn">unsupported browser</StatusPill>
            )}
          </div>
        </div>

        {error && <p className="mt-4 text-xs text-rose-300/80">{error}</p>}
        {success && <p className="mt-4 text-xs text-emerald-300/80">{success}</p>}

        <div className="mt-6 pt-6 border-t border-cream/[0.06]">
          <div className="text-[10px] uppercase tracking-[0.22em] text-cream/40 mb-3">
            Registered ({creds.length})
          </div>
          {passkeys.isLoading && <div className="text-sm text-cream/45">Loading…</div>}
          {hasNone && (
            <div className="text-sm text-cream/55">
              No passkeys yet. Add one above to sign in faster next time.
            </div>
          )}
          {creds.length > 0 && (
            <div className="space-y-2">
              {creds.map((c) => (
                <div key={c.credential_id} className="flex items-center justify-between gap-4 py-2 border-b border-cream/[0.04] last:border-0">
                  <div className="min-w-0">
                    <div className="text-sm text-cream/85 truncate">
                      {summarizeUserAgent(c.registered_user_agent)}
                    </div>
                    <div className="text-[11px] text-cream/45 mt-0.5">
                      Added {new Date(c.registered_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      {c.last_used_at && ` · last used ${relativeTime(c.last_used_at)}`}
                    </div>
                  </div>
                  <StatusPill tone="ok">active</StatusPill>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-6 pt-6 border-t border-cream/[0.06]">
          <dl className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-3 text-sm">
            <Row label="Magic-link TTL">15 minutes</Row>
            <Row label="Session TTL">30 days (sliding)</Row>
            <Row label="Multi-factor">Passkey OR magic-link</Row>
          </dl>
        </div>
      </Card>
    </section>
  );
}

function summarizeUserAgent(ua: string | null): string {
  if (!ua) return 'Unknown device';
  if (/iPhone|iPad/.test(ua)) return 'iPhone / iPad';
  if (/Mac/.test(ua) && /Safari/.test(ua) && !/Chrome/.test(ua)) return 'Safari on Mac';
  if (/Mac/.test(ua) && /Chrome/.test(ua)) return 'Chrome on Mac';
  if (/Android/.test(ua)) return 'Android device';
  if (/Windows/.test(ua) && /Edg/.test(ua)) return 'Edge on Windows';
  if (/Windows/.test(ua) && /Chrome/.test(ua)) return 'Chrome on Windows';
  if (/Firefox/.test(ua)) return 'Firefox';
  return 'Browser';
}

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  if (ms < 3600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86400_000) return `${Math.floor(ms / 3600_000)}h ago`;
  if (ms < 7 * 86400_000) return `${Math.floor(ms / 86400_000)}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
